import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Grid,
  FormLabel,
  OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  SmartToy as BotIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { API_BASE_URL } from '../../api';
import { botsApi } from '../../api/bots';
import { StatusBadge } from '../../components/common/StatusBadge';
import { useToast } from '../../hooks/useToast';
import { EmptyState } from '../../components/common/EmptyState';
import { getUser } from '../../api/auth';

const K = {
  navy: '#121826',
  blue: '#4185F1',
  border: 'rgba(18, 24, 38, 0.08)',
  surface: '#F8FAFC',
};

const kobraPageSx = { p: 3, width: '100%', maxWidth: { sm: '100%', md: '1700px' }, bgcolor: K.surface };
const kobraPrimaryBtnSx = {
  borderRadius: 1.5,
  px: 3,
  py: 1.25,
  textTransform: 'none',
  fontWeight: 700,
  bgcolor: K.blue,
  color: '#fff',
  border: 'none',
  boxShadow: `0 4px 14px ${alpha(K.blue, 0.35)}`,
  '&:hover': { bgcolor: '#3574d9', boxShadow: `0 6px 18px ${alpha(K.blue, 0.38)}`, border: 'none' },
};
const kobraCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 2,
  border: `1px solid ${K.border}`,
  boxShadow: '0 1px 4px rgba(18, 24, 38, 0.06)',
  transition: 'all .2s ease',
  '&:hover': { boxShadow: '0 6px 18px rgba(18, 24, 38, 0.12)', transform: 'translateY(-2px)' },
};

export const BotsManagement = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    plataforma: 'whatsapp(support)',
    descripcion: '',
    idioma: 'es',
    status: 'active',
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const user = getUser();
  const { data: bots, isLoading } = useQuery({
    queryKey: ['bots'],
    queryFn: () => botsApi.getBots(),
  });

  const createMutation = useMutation({
    mutationFn: (data) => botsApi.createBot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] });
      setDialogOpen(false);
      showToast('Bot creado exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al crear bot', 'error');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => botsApi.updateBot(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] });
      setDialogOpen(false);
      showToast('Bot actualizado exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al actualizar bot', 'error');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => botsApi.deleteBot(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots'] });
      showToast('Bot eliminado exitosamente', 'success');
    },
    onError: (error) => {
      showToast(error.message || 'Error al eliminar bot', 'error');
    },
  });

  const handleCreate = () => {
    setEditingBot(null);
    setFormData({
      nombre: '',
      plataforma: 'whatsapp(support)',
      descripcion: '',
      idioma: 'es',
      status: 'active',
    });
    setDialogOpen(true);
  };

  const handleEdit = (bot) => {
    setEditingBot(bot);
    setFormData({
      nombre: bot.nombre || '',
      plataforma: bot.plataforma || 'whatsapp(support)',
      descripcion: bot.descripcion || '',
      idioma: bot.idioma || 'es',
      status: bot.status || 'active',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      nombre: '',
      plataforma: 'whatsapp(support)',
      descripcion: '',
      idioma: 'es',
      status: 'active',
    });
  };

  const handleSave = () => {
    if (editingBot) {
      updateMutation.mutate({ id: editingBot.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este bot?')) {
      deleteMutation.mutate(id);
    }
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/endpoints-assistent/recordatorio1`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Company-ID': user?.companyId,
      },
      body: JSON.stringify({})
    })
      .then(response => response.json())
      .then(data => {
        // You can log or use the data as needed
        // console.log('Test API /api/bots/test result:', data);
      })
      .catch(error => {
        alert(error);
        // Optionally handle errors
        // console.error('Error calling /api/bots/test:', error);
      });
      
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={kobraPageSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(K.blue, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BotIcon sx={{ fontSize: 40, color: K.blue }} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
              Gestión de Bots
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Administra y configura tus bots de automatización
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={kobraPrimaryBtnSx}
        >
          Crear Bot
        </Button>
      </Box>
      {!bots?.length ? (
        <EmptyState
          icon={BotIcon}
          title="No hay bots"
          description="Crea tu primer bot para automatizar las interacciones con clientes"
          actionLabel="Crear Bot"
          onAction={handleCreate}
        />
      ) : (
        <Grid container spacing={3}>
          {bots.map((bot) => (
            <Grid item xs={12} sm={6} md={4} key={bot.id}>
              <Card sx={kobraCardSx}>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: alpha(K.blue, 0.12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BotIcon sx={{ fontSize: 28, color: K.blue }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }} noWrap>
                          {bot.nombre}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {bot.plataforma}
                        </Typography>
                      </Box>
                    </Box>
                    <StatusBadge status={bot.status} />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Idioma:
                      </Typography>
                      <Chip
                        label={bot.idioma?.toUpperCase() || 'ES'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor: 'action.selected',
                        }}
                      />
                    </Box>

                    {bot.descripcion && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                          Descripción:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            color: 'text.primary',
                            lineHeight: 1.6,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {bot.descripcion.length > 150
                            ? `${bot.descripcion.substring(0, 150)}...`
                            : bot.descripcion}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'flex-end',
                      pt: 2,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(bot)}
                      sx={{
                        color: K.blue,
                        '&:hover': {
                          backgroundColor: alpha(K.blue, 0.1),
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(bot.id)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.light',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${K.border}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: alpha(K.blue, 0.12),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BotIcon sx={{ color: K.blue, fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingBot ? 'Editar Bot' : 'Crear Bot'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, bgcolor: '#fff' }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Nombre
            </Typography>
            <TextField
              fullWidth
              placeholder="Nombre del bot"
              value={formData.nombre}
              onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Plataforma
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.plataforma}
                onChange={(e) => setFormData({ ...formData, plataforma: e.target.value })}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 1.5,
                }}
              >
                <MenuItem value="whatsapp(support)">WhatsApp (Support)</MenuItem>
                <MenuItem value="whatsapp(reminders)">WhatsApp (Reminders)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Idioma
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.idioma}
                onChange={(e) => setFormData({ ...formData, idioma: e.target.value })}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 1.5,
                }}
              >
                <MenuItem value="es">Español (ES)</MenuItem>
                <MenuItem value="en">English (EN)</MenuItem>
                <MenuItem value="pt">Português (PT)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Descripción
            </Typography>
            <OutlinedInput
              fullWidth
              multiline
              rows={8}
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Ingresa las instrucciones o descripción del bot..."
              sx={{
                backgroundColor: '#fff',
                borderRadius: 1.5,
                '& textarea': {
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  padding: '14px',
                  boxSizing: 'border-box',
                  width: '100%',
                  overflow: 'auto',
                  overflowWrap: 'break-word',
                  wordWrap: 'break-word',
                },
                '& fieldset': {
                  borderColor: '#E0E0E0',
                },
                '&:hover fieldset': {
                  borderColor: K.blue,
                },
                '&.Mui-focused fieldset': {
                  borderColor: K.blue,
                },
              }}
            />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
              Esto se usará como las instrucciones/prompt del sistema del bot
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
              Estado
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                sx={{
                  backgroundColor: '#fff',
                  borderRadius: 1.5,
                }}
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
                <MenuItem value="testing">Prueba</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending || !formData.nombre}
            sx={kobraPrimaryBtnSx}
          >
            {createMutation.isPending || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

